/**
 * useReducedMotion hook.
 *
 * Returns true when the user has indicated they prefer reduced motion
 * via the 'prefers-reduced-motion: reduce' media query.
 *
 * All animated components should check this hook and:
 * - Skip decorative animations when true (ring breathing, page transitions, etc.)
 * - Retain functional state changes (color swap cyan to red on alert)
 *
 * Updates reactively if the user changes their OS accessibility setting mid-session.
 *
 * @returns true if the user prefers reduced motion
 */
import { useEffect, useState } from 'react';

const MEDIA_QUERY = '(prefers-reduced-motion: reduce)';

export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(() => {
    // SSR guard: window is not defined during server rendering
    if (typeof window === 'undefined') return false;
    return window.matchMedia(MEDIA_QUERY).matches;
  });

  useEffect(() => {
    const mq = window.matchMedia(MEDIA_QUERY);

    const onChange = (e: MediaQueryListEvent) => {
      setReduced(e.matches);
    };

    // Use addEventListener (modern API) - addListener is deprecated
    mq.addEventListener('change', onChange);

    return () => {
      mq.removeEventListener('change', onChange);
    };
  }, []);

  return reduced;
}
